import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

// Lazy load pages
const Dashboard = lazy(() => import('./pages/Dashboard').then(module => ({ default: module.Dashboard })));
const Auth = lazy(() => import('./pages/Auth').then(module => ({ default: module.Auth })));
const Projects = lazy(() => import('./pages/Projects').then(module => ({ default: module.Projects })));
const Reports = lazy(() => import('./pages/Reports').then(module => ({ default: module.Reports })));
const CreateReport = lazy(() => import('./pages/CreateReport').then(module => ({ default: module.CreateReport })));
const ReportDetail = lazy(() => import('./pages/ReportDetail').then(module => ({ default: module.ReportDetail })));
const Panel = lazy(() => import('./pages/Panel').then(module => ({ default: module.Panel })));
const CompanyUsers = lazy(() => import('./pages/CompanyUsers').then(module => ({ default: module.CompanyUsers })));
// Old landing page — deactivated, kept in src/pages/Landing.tsx for reference.
const AuditLanding = lazy(() => import('./pages/AuditLanding').then(module => ({ default: module.AuditLanding })));
const CompanyCreation = lazy(() => import('./pages/CompanyCreation').then(module => ({ default: module.CompanyCreation })));
const PitchDeck = lazy(() => import('./pages/PitchDeck').then(module => ({ default: module.PitchDeck })));
const ExternalUpload = lazy(() => import('./pages/ExternalUpload').then(module => ({ default: module.ExternalUpload })));
const Account = lazy(() => import('./pages/Account').then(module => ({ default: module.Account })));

import { InviteHandler } from './components/InviteHandler';

const PrivateRoute = ({ children }: { children: React.ReactNode }) => {
  const user = localStorage.getItem('user');
  return user ? (
    <>
      <InviteHandler />
      {children}
    </>
  ) : (
    <Navigate to="/auth" replace />
  );
};

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen bg-[#0f0f0f]">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
  </div>
);

function App() {
  return (
    <Router>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<AuditLanding />} />
          <Route path="/create-company" element={<CompanyCreation />} />
          <Route path="/pitch-deck" element={<PitchDeck />} />
          <Route path="/upload/:userId/:projectId" element={<ExternalUpload />} />
          <Route path="/auth" element={<Auth />} />
          <Route 
            path="/dashboard" 
            element={
              <PrivateRoute>
                <Dashboard />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/projects" 
            element={
              <PrivateRoute>
                <Projects />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/reports" 
            element={
              <PrivateRoute>
                <Reports />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/create-report" 
            element={
              <PrivateRoute>
                <CreateReport />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/reports/:id" 
            element={
              <PrivateRoute>
                <ReportDetail />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/panel" 
            element={
              <PrivateRoute>
                <Panel />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/company-users" 
            element={
              <PrivateRoute>
                <CompanyUsers />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/account" 
            element={
              <PrivateRoute>
                <Account />
              </PrivateRoute>
            } 
          />
          {/* Redirect unknown routes to home, which will then redirect to auth if not logged in */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </Router>
  );
}

export default App;
